import {
  Alert,
  AlertDescription,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { ACCESS_TOKEN_KEY } from "../../../constants";
import useAuthStore from "../../../store/auth";
import PasswordInput from "../../ui/password-input";

type LoginFormData = {
  username: string;
  password: string;
};

type LoginFormProps = {
  closeModal: () => void;
  setIsLoading: (loading: boolean) => void;
};

const LoginForm = ({ closeModal, setIsLoading }: LoginFormProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const [showError, setShowError] = useState(false);
  const [setUser, setIsAuthenticated] = useAuthStore(state => [
    state.setUser,
    state.setIsAuthenticated,
  ]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);

    const authToken = await api.login(data.username, data.password);
    if (!authToken) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    // store access token and set user
    localStorage.setItem(ACCESS_TOKEN_KEY, authToken.access_token);

    const user = await api.getCurrentUser();
    if (!user) {
      setShowError(true);
      setIsLoading(false);
      return;
    }
    setUser(user);
    setIsAuthenticated(true);
    setIsLoading(false);
    closeModal();
    navigate("/booking");
  };

  return (
    <form id="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* error */}
      {showError && (
        <Alert status="error" mb="0.5rem">
          <AlertIcon />
          <AlertDescription>{t("login.invalid_credentials")}</AlertDescription>
        </Alert>
      )}

      {/* username */}
      <FormControl isRequired isInvalid={!!errors.username}>
        <FormLabel htmlFor="username">Username</FormLabel>
        <Input
          type="text"
          id="username"
          {...register("username", { required: "username is required" })}
        />
      </FormControl>

      {/* password */}
      <FormControl mt={4} isRequired isInvalid={!!errors.password}>
        <FormLabel htmlFor="password">Password</FormLabel>
        <PasswordInput
          id="password"
          {...register("password", { required: "password is required" })}
        />
      </FormControl>
    </form>
  );
};

export default LoginForm;
